import React, { useEffect, useState } from 'react';
import { FlyToInterpolator } from 'react-map-gl';
import Supercluster from 'supercluster';
import { point } from '@turf/helpers';
import each from 'lodash/each';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import ListingMarker from './ListingMarker';
import ListingCluster from './ListingCluster';

const clusterProperties = {
  minZoom: 0,
  maxZoom: 17,
  radius: 60,
};

let supercluster;
let debouncedRecalculate;

let firstSearch = true;

function buildCluster(markers) {
  const cluster = new Supercluster(clusterProperties);

  const points = markers.map((listingMarker) => point(
    [listingMarker.longitude, listingMarker.latitude],
    { id: listingMarker.id },
  ));

  cluster.load(points);

  return cluster;
}

const ListingClusters = (props) => {
  const dispatch = useDispatch();
  const { map, markers } = props;
  const [clusters, setClusters] = useState([]);
  const { latitude, longitude, zoom } = useSelector((state) => state.map);

  /**
   * Function that renders the clusters and markers
   */
  function renderMarkers(clusters, clusterClickHandler) {
    return clusters.map((cluster) => {
      if (cluster.properties.cluster) {
        return (
          <ListingCluster
            key={`cluster-${cluster.id}`}
            cluster={cluster}
            onClick={clusterClickHandler}
          />
        );
      }

      return (
        <ListingMarker
          key={`marker-${cluster.properties.id}`}
          feature={cluster}
        />
      );
    });
  }

  // Use memoization on the renderMarkers() function to improve performance
  const renderedMarkers = React.useMemo(() => renderMarkers(clusters, clusterClickHandler),
    [clusterClickHandler, clusters]);

  /**
   * Cluster click handler that updates the viewport
   */
  function clusterClickHandler(cluster) {
    const zoom = supercluster.getClusterExpansionZoom(cluster.id);
    const [longitude, latitude] = cluster.geometry.coordinates;
    dispatch({
      type: 'SET_MAP_VIEWPORT',
      payload: {
        longitude,
        latitude,
        zoom,
        transitionDuration: 250,
      },
    });
  }

  /**
   * Rebuild the MARKER_CLUSTER_ASSOCIATION object that allows the association between a listing id and marker or cluster
   * Allows highlighting a marker or cluster when hovering a listing card
   */
  function buildMarkerClusterAssociation(clusters) {
    const data = {};

    each(clusters, (feature) => {
      const isCluster = !!feature.properties.cluster;
      if (isCluster) {
        const clusterId = feature.id;
        const markers = supercluster.getLeaves(feature.id, Infinity);
        each(markers, (marker) => {
          data[marker.properties.id] = `cluster-${clusterId}`;
        });
      } else {
        const markerId = feature.properties.id;
        data[markerId] = `marker-${markerId}`;
      }
    });

    dispatch({ type: 'SET_MAP_MARKER_CLUSTER_ASSOCIATION', payload: data });
  }

  /**
   * Function that 'recalculates' all the clusters within the visible bounding box/viewport
   */
  function recalculate() {
    const zoom = map.getZoom();
    const bounds = map.getBounds().toArray();
    const center = map.getCenter();
    const bbox = [...bounds[0], ...bounds[1]];
    const visibleClusters = supercluster.getClusters(bbox, Math.floor(zoom));

    let readableBbox;
    if (!firstSearch) {
      readableBbox = {
        zoom,
        latitude_gteq: bbox[1],
        latitude_lteq: bbox[3],
        longitude_gteq: bbox[0],
        longitude_lteq: bbox[2],
        latitude: center.lat,
        longitude: center.lng,
      };

      // Update search filters bounding box
      dispatch({ type: 'UPDATE_BOUNDING_BOX', payload: readableBbox });
    }

    firstSearch = false;

    buildMarkerClusterAssociation(visibleClusters);
    setClusters(visibleClusters);
  }

  useEffect(() => {
    debouncedRecalculate = debounce(recalculate, 150);
  }, [recalculate]);

  useEffect(() => {
    if (supercluster) {
      debouncedRecalculate.cancel();
      debouncedRecalculate(supercluster);
    }
  }, [latitude, longitude, zoom]);

  useEffect(() => {
    supercluster = buildCluster(markers);

    debouncedRecalculate.cancel();
    debouncedRecalculate(supercluster);
  }, [markers]);

  return (
    <>
      {renderedMarkers}
    </>
  );
};

export default ListingClusters;
